// Temporary EXR mock for the SHARED admin season endpoints (create/get/update/settings/save-population) (GMTEC-23994)
// that flow through GeneralSeasonService & SeasonSettingsService and can't be swapped per season type.
// Everything EXR-specific should be a traditional mock instead (see exr-season-store.ts).
// TODO: Replace with the real EXR backend; delete this together with exr-season-store.ts. (GMTEC-23994)
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FeatureFlagsService } from '@coin/shared/core/data-access';
import { SeasonProcessState, SeasonTypeEnum, TransactionState } from '@coin/shared/core/util-enums';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';
import {
  createExrSeason,
  getExrSeason,
  getExrSeasonSettings,
  saveExrSeasonSettings,
  updateExrSeason
} from './exr-season-store';
import { getExrMonitoringMissingRequirements } from './exr-monitoring-validation';

@Injectable()
export class ExrMockInterceptor implements HttpInterceptor {
  private readonly featureFlags = inject(FeatureFlagsService);

  private static readonly TX_PREFIX = 'exr-mock-tx-';

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.featureFlags.isActive('exceptionalRecognitions')) {
      return next.handle(req);
    }

    const path = new URL(req.url, window.location.origin).pathname;
    const method = req.method.toUpperCase();
    const body = req.body as Record<string, unknown>;

    // Only the mock's own transaction ids — every other transaction-status request passes through.
    const txMatch = path.match(/\/transaction-status\/([^/]+)$/);
    if (method === 'GET' && txMatch?.[1].startsWith(ExrMockInterceptor.TX_PREFIX)) {
      return this.respond(this.finishedTransaction(txMatch[1]));
    }

    const settingsByIdMatch = path.match(/\/exceptional-recognition\/settings\/seasons\/([^/]+)$/);
    if (method === 'GET' && settingsByIdMatch) {
      return this.respond(getExrSeasonSettings(settingsByIdMatch[1]));
    }
    if (method === 'PUT' && /\/exceptional-recognition\/settings$/.test(path)) {
      return this.respond(saveExrSeasonSettings(body));
    }

    // The seasons LIST is a traditional mock (ExceptionalRecognitionSeasonService) and never hits the network.
    if (path.includes('/exceptional-recognition/seasons')) {
      const savePopMatch = path.match(/\/seasons\/([^/]+)\/save-population$/);
      if (method === 'POST' && savePopMatch) {
        return this.respond(this.startedTransaction(savePopMatch[1]));
      }
      const updateStateMatch = path.match(/\/exceptional-recognition\/seasons\/([^/]+)\/update-state$/);
      if (method === 'PATCH' && updateStateMatch) {
        return this.respondToStateTransition(updateStateMatch[1], body?.['state'] as SeasonProcessState);
      }
      const byIdMatch = path.match(/\/exceptional-recognition\/seasons\/([^/]+)$/);
      if (method === 'GET' && byIdMatch) {
        return this.respond(getExrSeason(byIdMatch[1]));
      }
      if (method === 'POST' && /\/exceptional-recognition\/seasons$/.test(path)) {
        return this.respond(createExrSeason(body));
      }
    }

    // updateSeason() PUTs every type to /merit/seasons, so match by body to catch only EXR ones.
    if (
      method === 'PUT' &&
      /\/merit\/seasons$/.test(path) &&
      body?.['seasonType'] === SeasonTypeEnum.ExceptionalRecognitions
    ) {
      return this.respond(updateExrSeason(body));
    }

    return next.handle(req);
  }

  // The real backend validates Monitoring-readiness inside the transition endpoint and rejects with the unmet
  // requirements; the mock mirrors that here. Other transitions just persist the new state.
  private respondToStateTransition(seasonId: string, newState: SeasonProcessState): Observable<HttpEvent<unknown>> {
    if (newState === SeasonProcessState.Monitoring) {
      const missingRequirements = getExrMonitoringMissingRequirements(getExrSeasonSettings(seasonId));
      if (missingRequirements.length) {
        return throwError(() => new HttpErrorResponse({ status: 400, error: { missingRequirements } })).pipe(
          delay(150)
        );
      }
    }
    return this.respond(updateExrSeason({ id: seasonId, state: newState }));
  }

  private startedTransaction(entityId: string): Record<string, unknown> {
    const now = new Date().toISOString();
    return {
      transactionId: `${ExrMockInterceptor.TX_PREFIX}${crypto.randomUUID()}`,
      entityId,
      startedAt: now,
      updatedAt: now,
      finishedAt: now,
      state: TransactionState.Started,
      progressCount: 0,
      totalCount: 1,
      processTitle: 'EXR mock',
      metadata: {}
    };
  }

  private finishedTransaction(transactionId: string): Record<string, unknown> {
    const now = new Date().toISOString();
    return {
      transactionId,
      entityId: '',
      startedAt: now,
      updatedAt: now,
      finishedAt: now,
      state: TransactionState.Finished,
      progressCount: 1,
      totalCount: 1,
      processTitle: 'EXR mock',
      metadata: {
        successCount: 1,
        failureCount: 0,
        warningsCount: 0,
        totalCount: 1,
        modifiedEntities: [],
        downloadUri: '',
        failures: [],
        warnings: []
      }
    };
  }

  private respond<T>(responseBody: T): Observable<HttpEvent<T>> {
    return of(new HttpResponse({ status: 200, body: responseBody })).pipe(delay(150));
  }
}
